import type { ReactNode } from "react";

import { cx } from "@its-engineering-and-development/ui-foundation/utils";

export const BANNER_TONE = {
    /** Red styling for errors or blocking issues. */
    Error: "error",
    /** Orange styling for softer warnings. */
    Warning: "warning",
} as const;

export type BannerTone = (typeof BANNER_TONE)[keyof typeof BANNER_TONE];

interface BannerProps {
    /** Primary label. */
    title: string;
    /** Optional secondary line shown below the title. */
    description?: string;
    /** Visual tone of the banner. */
    tone?: BannerTone;
    /** Optional node rendered inline on the right side of the banner (e.g. an action link). */
    action?: ReactNode;
}

const BANNER_STYLES: Record<BannerTone, { container: string; title: string; description: string }> = {
    [BANNER_TONE.Error]: { container: "border-red-200 bg-red-50", title: "text-red-600", description: "text-red-600" },
    [BANNER_TONE.Warning]: { container: "border-orange-200 bg-orange-50", title: "text-orange-500", description: "text-orange-600" },
};

/**
 * Generic inline banner used to surface a status message with an optional description.
 */
export function Banner({ title, description, tone = BANNER_TONE.Error, action }: BannerProps) {
    const styles = BANNER_STYLES[tone];

    return (
        <div className={cx("flex items-center justify-between gap-3 rounded-md border px-4 py-3", styles.container)}>
            <div>
                <p className={cx("text-sm font-semibold", styles.title)}>{title}</p>
                {description && <p className={cx("mt-0.5 text-xs font-medium", styles.description)}>{description}</p>}
            </div>
            {action}
        </div>
    );
}
